import { Component, OnDestroy, OnInit } from '@angular/core';
import { Subscription } from 'rxjs';
import { finalize, skip } from 'rxjs/operators';
import { saveAs } from 'file-saver';
import { NzMessageService } from 'ng-zorro-antd/message';
import {
  WorkshopService,
  WorkshopFilterVO,
  WorkshopMasterDataQuery,
  WorkshopMasterDataStatsVO,
  WorkshopRemainPointsGrowthVO,
  WorkshopTargetVO,
  WorkshopMasterDataVO,
  WorkshopSelloutCbfSummaryVO,
  PerformanceDistributionVO,
  WorkshopAmountSumVO,
  PageResult,
} from '../../services/workshop.service';
import { CustomerService } from 'src/app/services/customer.service';

interface WorkshopStatusOverview {
  active: number;
  new: number;
  frozen: number;
  total: number;
}

interface RedemptionSummary {
  count: number;
  trendPercent: number;
  trendUp: boolean;
  trendFlat: boolean;
  description: string;
}

interface TypePerformanceItem {
  type: string;
  color: string;
  bgColor: string;
  target: string;
  achieved: number;
  total: number;
  unit: string;
  percent: number;
}

interface WorkshopRow {
  id: string;
  type: string;
  typeColor: string;
  name: string;
  region: string;
  address: string;
  status: string;
  statusColor: string;
  points: number;
  pointsColor: string;
  lastRedemption: string;
  selected?: boolean;
}

interface PerformanceDistribution {
  label: string;
  value: number;
}

interface TopPerformer {
  rank: number;
  name: string;
  amount: string;
  rankBgColor: string;
  rankTextColor: string;
}

interface ProductLineRow {
  workshopId: string;
  workshopName: string;
  productLine: string;
  cbfCNName: string;
  productLineColor: string;
  quantity: string;
  salesAmount: string;
  yoyGrowth: string;
  yoyUp: boolean;
  yoyAvailable: boolean;
  momGrowth: string;
  momUp: boolean;
  momAvailable: boolean;
}

interface PaginationState {
  total: number;
  pages: number;
}

type MultiFilterKey = 'month' | 'cbfName' | 'workshopType';

@Component({
  selector: 'app-work-shop',
  templateUrl: './work-shop.component.html',
  styleUrls: ['./work-shop.component.less']
})
export class WorkShopComponent implements OnInit, OnDestroy {
  searchText = '';
  readonly currentYear = new Date().getFullYear().toString();
  selectedYear = this.currentYear;
  selectedMonth: string[] = [];
  selectedCbfName: string[] = [];
  selectedWorkshopType: string[] = [];
  readonly monthOptions = Array.from({ length: 12 }, (_, index) => String(index + 1).padStart(2, '0'));
  yearOptions: string[] = [];
  cbfNameOptions: string[] = [];
  workshopTypeOptions: string[] = [];

  statusOverview: WorkshopStatusOverview = { active: 0, new: 0, frozen: 0, total: 0 };
  redemptionSummary: RedemptionSummary = { count: 0, trendPercent: 0, trendUp: true, trendFlat: true, description: '' };
  workshopTypePerformance: TypePerformanceItem[] = [];
  salesStorePerformance: TypePerformanceItem[] = [];
  workshopRows: WorkshopRow[] = [];
  performanceDistributions: PerformanceDistribution[] = [];
  topPerformers: TopPerformer[] = [];
  productLineRows: ProductLineRow[] = [];
  selectedWorkshop: WorkshopRow | null = null;
  statusOverviewLoading = false;
  redemptionLoading = false;
  workshopTypeLoading = false;
  salesStoreLoading = false;
  workshopTypeLoaded = false;
  salesStoreLoaded = false;
  workshopTableLoading = false;
  performanceLoading = false;
  productLineLoading = false;
  masterDataExportLoading = false;

  pageSize = 4;
  currentPage = 1;
  totalRecords = 0;
  totalPages = 0;

  productPageSize = 10;
  productCurrentPage = 1;
  productTotalRecords = 0;
  productTotalPages = 0;

  private customerSubscription?: Subscription;

  private readonly typeColorPalette = [
    { color: '#007bc0', bgColor: '#d1e4ff' }, // 蓝
    { color: '#9e2896', bgColor: '#f0dcee' }, // 紫
    { color: '#00884a', bgColor: '#b8efc9' }, // 绿
    { color: '#f19f23', bgColor: '#ffd697' }, // 橙
  ];

  private readonly typeColorAssignments = new Map<string, { color: string; bgColor: string }>();

  private readonly statusDisplayMap: Record<string, { label: string; color: string }> = {
    '01': { label: '创建', color: '#007bc0' },
    '02': { label: '活跃', color: '#00884a' },
    '03': { label: '冻结', color: '#71767c' },
    '活跃': { label: '活跃', color: '#00884a' },
    'active': { label: '活跃', color: '#00884a' },
    '已创建': { label: '创建', color: '#007bc0' },
    '创建': { label: '创建', color: '#007bc0' },
    'created': { label: '创建', color: '#007bc0' },
    '新建': { label: '创建', color: '#007bc0' },
    'new': { label: '创建', color: '#007bc0' },
    '已冻结': { label: '冻结', color: '#71767c' },
    '冻结': { label: '冻结', color: '#71767c' },
    'frozen': { label: '冻结', color: '#71767c' },
  };

  private readonly cbfColorMap: Record<string, string> = {
    '机油': '#00629a',
    '滤清器': '#9e28b5',
    '蓄电池': '#008ecf',
    '刹车系统': '#00884a',
    '火花塞': '#f26f21',
    '雨刮片': '#0891b2',
  };

  constructor(
    private workshopService: WorkshopService,
    private customerService: CustomerService,
    private message: NzMessageService,
  ) {}

  get pagedWorkshopRows(): WorkshopRow[] {
    return this.workshopRows;
  }

  get statusBarWidths() {
    const sum = this.statusOverview.active + this.statusOverview.new + this.statusOverview.frozen;
    if (sum === 0) return { active: '0%', new: '0%', frozen: '0%' };
    return {
      active: (this.statusOverview.active / sum * 100) + '%',
      new: (this.statusOverview.new / sum * 100) + '%',
      frozen: (this.statusOverview.frozen / sum * 100) + '%'
    };
  }

  get selectedWorkshopPeriod(): string {
    const lastRedemption = this.selectedWorkshop?.lastRedemption;
    if (!lastRedemption || lastRedemption === '-') {
      return '-';
    }

    const match = lastRedemption.match(/^(\d{4})-(\d{2})/);
    if (!match) {
      return '-';
    }

    return `${match[1]}年${match[2]}月`;
  }

  formatPoints(points: number): string {
    return points?.toLocaleString() ?? '0';
  }

  clearFilters(): void {
    this.searchText = '';
    this.selectedYear = this.yearOptions.includes(this.currentYear)
      ? this.currentYear
      : (this.yearOptions[0] || this.currentYear);
    this.selectedMonth = [];
    this.selectedCbfName = [];
    this.selectedWorkshopType = [];
    this.resetPagingAndProductView();
    this.loadMasterData();
  }

  onSearch(): void {
    this.resetPagingAndProductView();
    this.loadMasterData();
  }

  clearSearchText(): void {
    this.searchText = '';
    this.resetPagingAndProductView();
    this.loadMasterData();
  }

  onYearChange(year: string): void {
    this.selectedYear = year;
    this.resetPagingAndProductView();
    this.loadMasterData();
  }

  onDetailClick(row: WorkshopRow): void {
    this.selectWorkshop(row);
  }

  onCancelDetail(): void {
    if (!this.selectedWorkshop) {
      return;
    }
    this.selectedWorkshop = null;
    this.workshopRows.forEach(workshop => workshop.selected = false);
    this.productCurrentPage = 1;
    this.loadProductLineData();
  }

  onPageChange(page: number): void {
    if (page < 1 || page > this.totalPages || page === this.currentPage) {
      return;
    }
    this.currentPage = page;
    this.loadMasterDataPage();
  }

  onProductPageChange(page: number): void {
    if (page < 1 || page > this.productTotalPages || page === this.productCurrentPage) {
      return;
    }
    this.productCurrentPage = page;
    this.loadProductLineData();
  }

  onDownloadMasterData(): void {
    if (this.masterDataExportLoading) {
      return;
    }
    this.masterDataExportLoading = true;

    const query = this.buildFilterQuery();
    this.workshopService.exportMasterData(query)
      .pipe(finalize(() => this.masterDataExportLoading = false))
      .subscribe({
        next: async response => {
          const blob = response.body;
          if (!blob || blob.size === 0) {
            this.message.warning('暂无数据可导出');
            return;
          }

          // 后端鉴权失败 / 业务异常时，会以 application/json 返回标准 ResponseData，
          // 但因为前端声明 responseType: 'blob'，这里仍然会拿到一个 Blob。
          // 不能直接 saveAs，否则会得到一个内容是 JSON、扩展名是 .xlsx 的坏文件，
          // Excel 打开时报 "file format or file extension is not valid"。
          const contentType = (response.headers.get('Content-Type') || '').toLowerCase();
          if (contentType.includes('application/json') || contentType.includes('text/')) {
            try {
              const text = await blob.text();
              const payload = text ? JSON.parse(text) : null;
              const msg = payload?.rtnMsg || payload?.message || '下载失败，请稍后重试';
              this.message.error(msg);
            } catch {
              this.message.error('下载失败，请稍后重试');
            }
            return;
          }

          saveAs(blob, this.resolveExportFileName(response.headers.get('Content-Disposition')));
        },
        error: () => {
          this.message.error('下载失败，请稍后重试');
        },
      });
  }

  private resolveExportFileName(contentDisposition: string | null): string {
    const fallback = `workshop_master_data_${new Date().toISOString().slice(0, 10).replace(/-/g, '')}.xlsx`;
    if (!contentDisposition) {
      return fallback;
    }

    // 优先解析 RFC 5987 的 filename*=UTF-8'' 格式（中文/特殊字符更稳定）。
    const utf8Match = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
    if (utf8Match && utf8Match[1]) {
      try {
        return decodeURIComponent(utf8Match[1].trim());
      } catch {
        // 解码失败时继续走下面的兜底逻辑
      }
    }

    const plainMatch = contentDisposition.match(/filename="?([^";]+)"?/i);
    if (plainMatch && plainMatch[1]) {
      try {
        return decodeURIComponent(plainMatch[1].trim());
      } catch {
        return plainMatch[1].trim();
      }
    }

    return fallback;
  }

  ngOnInit(): void {
    this.loadFilterOptions();

    this.customerSubscription = this.customerService.selectedCustomer$
      .pipe(skip(1))
      .subscribe(() => {
        this.handleCustomerChange();
      });
  }

  ngOnDestroy(): void {
    this.customerSubscription?.unsubscribe();
  }

  onFilterChange(): void {
    this.resetPagingAndProductView();
    this.loadMasterData();
  }

  private handleCustomerChange(): void {
    this.resetPagingAndProductView();
    this.productLineRows = [];
    this.loadFilterOptions();
  }

  /**
   * 主数据筛选/搜索发生变化时，复位主数据分页 & 产品线视图：
   * 取消已选中的维修站，让产品线汇总回到「全部维修站」分页视图。
   */
  private resetPagingAndProductView(): void {
    this.currentPage = 1;
    this.productCurrentPage = 1;
    this.selectedWorkshop = null;
  }

  isAllSelected(selectedValues: string[], allOptions: string[]): boolean {
    return allOptions.length > 0 && selectedValues.length === allOptions.length;
  }

  toggleSelectAll(filterKey: MultiFilterKey): void {
    const allOptions = this.getFilterOptionsByKey(filterKey);
    const selectedValues = this.getSelectedValuesByKey(filterKey);
    const nextValues = this.isAllSelected(selectedValues, allOptions) ? [] : [...allOptions];

    this.setSelectedValuesByKey(filterKey, nextValues);
    this.onFilterChange();
  }

  private loadMasterData(): void {
    // 过去使用单一 /masterdata 聚合接口，数据量大时前端需要等所有计算都完成才能渲染。
    // 现在拆分成多个独立接口并行请求，每个卡片拿到数据后独立渲染，减少首屏阻塞。
    const baseQuery = this.buildFilterQuery();
    const pagedQuery: WorkshopMasterDataQuery = {
      ...baseQuery,
      pageNum: this.currentPage,
      pageSize: this.pageSize,
    };

    this.loadStatusStats(baseQuery);
    this.loadGrowth(baseQuery);
    this.loadTargetGroup(baseQuery);
    this.loadPerformanceDistribution(baseQuery);
    this.loadWorkshopTable(pagedQuery);
    this.loadProductLineData();
  }

  private loadStatusStats(query: WorkshopMasterDataQuery): void {
    this.statusOverviewLoading = true;
    this.workshopService.getStatusStats(query)
      .pipe(finalize(() => this.statusOverviewLoading = false))
      .subscribe(data => {
        this.statusOverview = this.buildStatusOverview(data || []);
      });
  }

  private loadGrowth(query: WorkshopMasterDataQuery): void {
    this.redemptionLoading = true;
    this.workshopService.getGrowth(query)
      .pipe(finalize(() => this.redemptionLoading = false))
      .subscribe(data => {
        this.redemptionSummary = this.buildRedemptionSummary(data);
      });
  }

  private loadTargetGroup(query: WorkshopMasterDataQuery): void {
    this.workshopTypeLoading = true;
    this.salesStoreLoading = true;
    this.workshopService.getTargetGroup(query)
      .pipe(finalize(() => {
        this.workshopTypeLoading = false;
        this.salesStoreLoading = false;
        this.workshopTypeLoaded = true;
        this.salesStoreLoaded = true;
      }))
      .subscribe(data => {
        this.workshopTypePerformance = this.buildTypePerformance(data?.workshopTargetVO || []);
        this.salesStorePerformance = this.buildTypePerformance(data?.salesStoreTargetVO || []);
      });
  }

  private loadPerformanceDistribution(query: WorkshopMasterDataQuery): void {
    this.performanceLoading = true;
    this.workshopService.getPerformanceDistribution(query)
      .pipe(finalize(() => this.performanceLoading = false))
      .subscribe(data => {
        if (!data) {
          this.performanceDistributions = [];
          this.topPerformers = [];
          return;
        }
        this.performanceDistributions = this.buildPerformanceDistributions(data);
        this.topPerformers = this.buildTopPerformers(data.workshopAmountSumVOS);
      });
  }

  private loadWorkshopTable(query: WorkshopMasterDataQuery): void {
    this.workshopTableLoading = true;
    this.workshopService.getMasterDataPage(query)
      .pipe(finalize(() => this.workshopTableLoading = false))
      .subscribe(data => {
        if (!data) {
          this.workshopRows = [];
          this.totalRecords = 0;
          this.totalPages = 0;
          return;
        }
        this.updateWorkshopTable(data);
      });
  }

  private loadFilterOptions(): void {
    this.workshopService.getFilterOptions()
      .subscribe(options => {
        this.applyFilterOptions(options);
        this.loadMasterData();
      });
  }

  private loadProductLineData(): void {
    this.productLineLoading = true;
    const query: WorkshopMasterDataQuery = {
      ...this.buildFilterQuery(),
      selectedClientId: this.selectedWorkshop?.id,
      pageNum: this.productCurrentPage,
      pageSize: this.productPageSize,
    };
    this.workshopService.getCbfSummary(query)
      .pipe(finalize(() => this.productLineLoading = false))
      .subscribe(data => {
        if (!data) {
          this.productLineRows = [];
          this.productTotalRecords = 0;
          this.productTotalPages = 0;
          return;
        }
        const pagination = this.getPaginationState(data, this.productPageSize);
        this.productLineRows = this.buildProductLineRows(data.records);
        this.productTotalRecords = pagination.total;
        this.productTotalPages = pagination.pages;
        this.productCurrentPage = this.clampPage(this.productCurrentPage, pagination.pages);
      });
  }

  private loadMasterDataPage(): void {
    this.workshopTableLoading = true;
    const query: WorkshopMasterDataQuery = {
      ...this.buildFilterQuery(),
      pageNum: this.currentPage,
      pageSize: this.pageSize,
    };
    this.workshopService.getMasterDataPage(query)
      .pipe(finalize(() => this.workshopTableLoading = false))
      .subscribe(data => {
        if (!data) {
          this.workshopRows = [];
          this.totalRecords = 0;
          this.totalPages = 0;
          return;
        }

        this.updateWorkshopTable(data);
      });
  }

  private selectWorkshop(row: WorkshopRow): void {
    this.workshopRows.forEach(workshop => workshop.selected = workshop.id === row.id);
    row.selected = true;
    this.selectedWorkshop = row;
    this.productCurrentPage = 1;
    this.loadProductLineData();
  }

  private updateWorkshopTable(pageData: PageResult<WorkshopMasterDataVO>): void {
    const pagination = this.getPaginationState(pageData, this.pageSize);
    this.workshopRows = this.buildWorkshopRows(pageData.records);
    this.totalRecords = pagination.total;
    this.totalPages = pagination.pages;
    this.currentPage = this.clampPage(this.currentPage, pagination.pages);
    this.syncSelectedWorkshopState();
  }

  private getPaginationState<T>(pageData: PageResult<T>, pageSize: number): PaginationState {
    const total = Number(pageData.total) || 0;
    const pages = Math.ceil(total / pageSize);
    return { total, pages };
  }

  private clampPage(page: number, totalPages: number): number {
    return Math.min(Math.max(page, 1), Math.max(totalPages, 1));
  }

  private syncSelectedWorkshopState(): void {
    if (!this.selectedWorkshop) {
      return;
    }

    this.workshopRows.forEach(workshop => {
      workshop.selected = workshop.id === this.selectedWorkshop?.id;
    });
  }

  private applyFilterOptions(options: WorkshopFilterVO | null | undefined): void {
    this.yearOptions = options?.yearList || [];
    this.cbfNameOptions = options?.cbfNames || [];
    this.workshopTypeOptions = options?.workshopTypes || [];
    this.assignTypeColors(this.workshopTypeOptions);

    if (this.yearOptions.length) {
      this.selectedYear = this.yearOptions.includes(this.selectedYear)
        ? this.selectedYear
        : (this.yearOptions.includes(this.currentYear) ? this.currentYear : this.yearOptions[0]);
    } else {
      this.selectedYear = this.currentYear;
    }

    this.selectedCbfName = this.selectedCbfName.filter(cbfName => this.cbfNameOptions.includes(cbfName));
    this.selectedMonth = this.selectedMonth.filter(month => this.monthOptions.includes(month));
    this.selectedWorkshopType = this.selectedWorkshopType.filter(workshopType =>
      this.workshopTypeOptions.includes(workshopType)
    );
  }

  private getFilterOptionsByKey(filterKey: MultiFilterKey): string[] {
    switch (filterKey) {
      case 'month':
        return this.monthOptions;
      case 'cbfName':
        return this.cbfNameOptions;
      case 'workshopType':
        return this.workshopTypeOptions;
    }
  }

  private getSelectedValuesByKey(filterKey: MultiFilterKey): string[] {
    switch (filterKey) {
      case 'month':
        return this.selectedMonth;
      case 'cbfName':
        return this.selectedCbfName;
      case 'workshopType':
        return this.selectedWorkshopType;
    }
  }

  private setSelectedValuesByKey(filterKey: MultiFilterKey, values: string[]): void {
    switch (filterKey) {
      case 'month':
        this.selectedMonth = values;
        return;
      case 'cbfName':
        this.selectedCbfName = values;
        return;
      case 'workshopType':
        this.selectedWorkshopType = values;
        return;
    }
  }

  private buildFilterQuery(): WorkshopMasterDataQuery {
    const searchText = this.searchText.trim();

    return {
      year: this.selectedYear,
      month: this.selectedMonth.length ? this.selectedMonth : undefined,
      cbfName: this.selectedCbfName.length ? this.selectedCbfName : undefined,
      workshopType: this.selectedWorkshopType.length ? this.selectedWorkshopType : undefined,
      fuzzySearch: searchText || undefined,
    };
  }

  private buildStatusOverview(stats: WorkshopMasterDataStatsVO[]): WorkshopStatusOverview {
    let active = 0, newCount = 0, frozen = 0, total = 0;
    for (const stat of (stats || [])) {
      const statusLabel = this.getStatusDisplay(stat.clientStatus).label;
      if (statusLabel === '活跃') {
        active = stat.statusCount || 0;
      } else if (statusLabel === '创建') {
        newCount = stat.statusCount || 0;
      } else if (statusLabel === '冻结') {
        frozen = stat.statusCount || 0;
      }
    }
    total = stats?.length > 0 ? (stats[0].totalClientCount || 0) : (active + newCount + frozen);
    return { active, new: newCount, frozen, total };
  }

  private buildRedemptionSummary(growth: WorkshopRemainPointsGrowthVO): RedemptionSummary {
    const rate = Number(growth?.growthRate ?? 0);
    const trendPercent = Math.abs(Math.round(rate * 100));
    const trendFlat = trendPercent === 0;
    const trendUp = !trendFlat && rate > 0;
    let description: string;
    if (trendFlat) {
      description = '平均每日活跃数量相较上周不变。';
    } else if (trendUp) {
      description = '平均每日活跃数量相较上周提升。';
    } else {
      description = '平均每日活跃数量相较上周下降。';
    }
    return {
      count: growth?.currentWeekClientCount ?? 0,
      trendPercent,
      trendUp,
      trendFlat,
      description
    };
  }

  private buildTypePerformance(targets: WorkshopTargetVO[]): TypePerformanceItem[] {
    return (targets || []).map(t => {
      const colors = this.getTypeColors(t.workshopType);
      const total = t.totalWorkshop || 0;
      const rawAchieved = t.targetAchWorkshop || 0;
      const targetValue = t.gtvPerClientType || 0;
      const isNoTarget = targetValue === 0;
      const achieved = isNoTarget ? total : rawAchieved;
      const percent = isNoTarget
        ? 100
        : (total > 0 ? Math.round(rawAchieved / total * 100) : 0);
      return {
        type: t.workshopType || '',
        color: colors.color,
        bgColor: colors.bgColor,
        target: `¥${this.formatNumber(t.gtvPerClientType)}/店`,
        achieved,
        total,
        unit: '店',
        percent
      };
    });
  }

  private buildWorkshopRows(records: WorkshopMasterDataVO[]): WorkshopRow[] {
    return (records || []).map(r => {
      const typeColors = this.getTypeColors(r.workshopType);
      const statusDisplay = this.getStatusDisplay(r.clientStatus);
      return {
        id: r.clientId || '',
        type: r.workshopType || '',
        typeColor: typeColors.color,
        name: r.clientName || '',
        region: r.marketName || '',
        address: r.detailAddress || '',
        status: statusDisplay.label,
        statusColor: statusDisplay.color,
        points: r.remainPoints || 0,
        pointsColor: '#c99700',
        lastRedemption: r.lastRedeemTime ? r.lastRedeemTime.substring(0, 10) : '-'
      };
    });
  }

  private getStatusDisplay(status: string | null | undefined): { label: string; color: string } {
    const normalizedStatus = (status || '').trim();
    const lowerCaseStatus = normalizedStatus.toLowerCase();
    return this.statusDisplayMap[normalizedStatus]
      || this.statusDisplayMap[lowerCaseStatus]
      || { label: normalizedStatus || '-', color: '#71767c' };
  }

  private buildPerformanceDistributions(dist: PerformanceDistributionVO): PerformanceDistribution[] {
    return [
      { label: '活跃率', value: Number(dist.activityRate ?? 0) },
      { label: '达标率', value: Number(dist.complianceRate ?? 0) }
    ];
  }

  private buildTopPerformers(vos: WorkshopAmountSumVO[]): TopPerformer[] {
    return (vos || []).slice(0, 4).map((v, i) => {
      const rank = i + 1;
      const colors = this.getRankColors(rank);
      return {
        rank,
        name: v.clientName || '',
        amount: this.formatAmountInThousands(v.amountSummary),
        rankBgColor: colors.bgColor,
        rankTextColor: colors.textColor
      };
    });
  }

  private buildProductLineRows(summaries: WorkshopSelloutCbfSummaryVO[]): ProductLineRow[] {
    return (summaries || []).map(s => {
      const yoy = this.buildGrowth(s.yoy);
      const mom = this.buildGrowth(s.yom);
      return {
        workshopId: s.clientId || this.selectedWorkshop?.id || '',
        workshopName: s.clientName || this.selectedWorkshop?.name || '',
        productLine: s.cbfName || '',
        cbfCNName: s.cbfCNName || '',
        productLineColor: this.cbfColorMap[s.cbfName] || '#6b7280',
        quantity: String(s.totalMtdQty ?? 0),
        salesAmount: `¥${this.formatNumber(s.totalMtdTurnover)}`,
        yoyGrowth: yoy.text,
        yoyUp: yoy.up,
        yoyAvailable: yoy.available,
        momGrowth: mom.text,
        momUp: mom.up,
        momAvailable: mom.available
      };
    });
  }

  private buildGrowth(value: number | string | null | undefined): { text: string; up: boolean; available: boolean } {
    const num = this.parseNumericValue(value);
    if (num == null) {
      return { text: '-', up: false, available: false };
    }

    const percent = num * 100;
    const up = percent >= 0;
    const sign = up ? '+' : '-';
    return {
      text: `${sign}${Math.abs(percent).toFixed(1)}%`,
      up,
      available: true
    };
  }

  private assignTypeColors(types: string[]): void {
    this.typeColorAssignments.clear();
    (types || [])
      .map(t => (t || '').trim())
      .filter(t => !!t)
      .forEach(t => this.getTypeColors(t));
  }

  private getTypeColors(type: string): { color: string; bgColor: string } {
    const normalizedType = (type || '').trim();
    if (!normalizedType) {
      return this.typeColorPalette[0];
    }

    const cached = this.typeColorAssignments.get(normalizedType);
    if (cached) {
      return cached;
    }

    const paletteSize = this.typeColorPalette.length;
    const assigned = this.typeColorPalette[this.typeColorAssignments.size % paletteSize];
    this.typeColorAssignments.set(normalizedType, assigned);
    return assigned;
  }

  private getRankColors(rank: number): { bgColor: string; textColor: string } {
    switch (rank) {
      case 1: return { bgColor: '#fef3c7', textColor: '#b45309' };
      case 2: return { bgColor: '#f1f5f9', textColor: '#334155' };
      case 3: return { bgColor: '#ffedd5', textColor: '#c2410c' };
      default: return { bgColor: '#eff1f2', textColor: '#595e62' };
    }
  }

  private formatAmountInThousands(value: number | string | null | undefined): string {
    const num = this.parseNumericValue(value);
    if (num == null) return '0K';
    return `${Math.round(num / 1000)}K`;
  }

  private formatNumber(value: number | string | null | undefined): string {
    const num = this.parseNumericValue(value);
    if (num == null) return '0';
    return num.toLocaleString();
  }

  private parseNumericValue(value: number | string | null | undefined): number | null {
    if (value == null) return null;
    if (typeof value === 'number') {
      return isNaN(value) ? null : value;
    }

    const normalizedValue = value.replace(/,/g, '').trim();
    if (!normalizedValue) return null;

    const parsedValue = Number(normalizedValue);
    return isNaN(parsedValue) ? null : parsedValue;
  }
}
